import { IConfigFunctionArgs } from 'Components/Worksheets/Core';
import { hasNonEmptyValue } from 'Lib/Any';
import { pick } from 'ramda';

export const getConfigValue = (config: any, name: string) => {
  if (!config || !name) return null;
  if (typeof name !== 'string') return null;

  // We need to pull out config that:
  // * is an exact match on the name provided
  // * is a "generalised" match - i.e. the name matches excluding array indices

  // We then merge the config together such that more specific matches take priority

  // We check for generalised matches from the top-level down.
  // This means you can have an index-specific config under a generalised config, but not the other way around
  let configNamesInAscendingPriority = [name];

  // Array indices may be expressed as [i] or .i
  const arrayIndexRegex = /\[[0-9]*\]|^[0-9]+(?:\.|$)|\.[0-9]+(?=\.|$)/;
  while (arrayIndexRegex.test(configNamesInAscendingPriority[0])) {
    configNamesInAscendingPriority.unshift(configNamesInAscendingPriority[0].replace(arrayIndexRegex, ''));
  }

  return mergeConfig(
    ...configNamesInAscendingPriority.filter((n) => hasNonEmptyValue(config[n])).map((n) => ({ [name]: config[n] }))
  )[name];
};

/**
 * Evaluates config as an object of properties for the given name,
 * where any functional config is evaluated with the provided arguments
 *
 * @returns Object of properties to set on field with the given name
 */
export const evaluateConfig = (config: any, name: string, args: IConfigFunctionArgs) => {
  let configByName = (name && getConfigValue(config, name)) || {};

  return typeof configByName === 'function' ? configByName(args) : configByName;
};

/**
 * Picks a set of properties from a given config object
 */
export const pickConfig = (config: { [key: string]: any }, properties: Array<string>) =>
  Object.fromEntries(
    Object.entries(config).map(([key, value]: [string, any]) => {
      if (typeof value === 'function') {
        // Wrap functional config in a new function that will evalute the function, then pick the properties we want
        return [key, (configArgs: IConfigFunctionArgs) => pick(properties, { ...value(configArgs) })];
      } else {
        return [key, pick(properties, value)];
      }
    })
  );

/**
 * Merges config objects with the later objects taking preference over the earlier
 *
 * We can implement other merge strategies, but it's not really required right now
 * (as we only use this to merge modifiers into a standard config)
 */
export const mergeConfig = (...configs: any[]) => {
  const merged: any = {};
  configs.forEach((confObject) => {
    Object.entries(confObject).forEach(([key, value]: [string, any]) => {
      let originalValue = merged[key];
      if (typeof originalValue === 'function' && typeof value === 'function') {
        merged[key] = (configArgs: any) => ({ ...originalValue(configArgs), ...value(configArgs) });
      } else if (typeof originalValue === 'function' && typeof value !== 'function') {
        merged[key] = (configArgs: any) => ({ ...originalValue(configArgs), ...value });
      } else if (typeof originalValue !== 'function' && typeof value === 'function') {
        merged[key] = (configArgs: any) => ({ ...originalValue, ...value(configArgs) });
      } else {
        merged[key] = { ...originalValue, ...value };
      }
    });
  });
  return merged;
};
/**
 * Wraps an equipment path into the structure required to be merged into
 * a field's config to define the equipment that measures that field
 */
export const measuredUsingConfigFor = (equipmentPath: string) => ({
  measuredUsingName: equipmentPath,
});

/**
 * Wraps provided paths into structure required to be merged into
 * a field's config to define which measurements the field was derived from
 */
export const wrapAsDerivedFromMeasurementsConfig = (measurementPaths: Array<string>) => ({
  derivedFromMeasurements: measurementPaths,
});

export type ConfigMapElement<T> = T | ((_: IConfigFunctionArgs) => T);

/**
 * Converts a map of field names to their labels to a config object
 */
export const convertLabelMapToConfig = (labelMap: { [name: string]: ConfigMapElement<string> }) =>
  convertMapToConfig(labelMap, 'label');

/**
 * Converts a map of field names to their label uom (where required to override "data" uom) to a config object
 */
export const convertLabelUomMapToConfig = (labelUomMap: { [name: string]: ConfigMapElement<string> }) =>
  convertMapToConfig(labelUomMap, 'labelUom');

/**
 * Converts a map of field names to property values to a config object with a property with a given name.
 */
const convertMapToConfig = <T>(map: { [name: string]: ConfigMapElement<T> }, configPropertyName: string) =>
  Object.fromEntries(
    Object.entries(map).map(([k, v]) => [
      k,
      // Where config map element is a function, wrap into a function returning the config object
      typeof v === 'function'
        ? (args: IConfigFunctionArgs) => ({ [configPropertyName]: (v as (i: IConfigFunctionArgs) => T)(args) })
        : // Otherwise return static config object
          { [configPropertyName]: v },
    ])
  );
